"use client";

import React, { useState, useRef, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { FaTimes, FaPaperPlane } from "react-icons/fa";

const AGENT_NAME = "Finn";

const QUICK_MESSAGES = [
  "What services do you offer?",
  "Get a Free Quote",
  "Mobile App Development",
  "Web Development",
  "AI Automation",
];

const BOT_RESPONSES = {
  "what services do you offer": "We offer Mobile App Development, Web Development, Graphic & UI/UX Design, IT Solutions & Consultancy, and AI Automation. Which service are you interested in?",
  "get a free quote": "I'd love to help you get a quote! Please share your project details — what kind of app or website do you need, and what's your timeline?",
  "mobile app development": "We build high-performance Android & iOS apps using React Native, Flutter, Swift, and Kotlin. Want to discuss your app idea?",
  "web development": "We create responsive, fast websites and web apps using React, Next.js, Node.js and more. Tell me about your project!",
  "ai automation": "We implement AI-powered automation to streamline your workflows, build chatbots, and integrate machine learning. What process would you like to automate?",
  "default": "Thanks for reaching out! Our team will get back to you shortly. You can also reach us at info@fiieve.com or call +44 7466 761773.",
};

function getBotResponse(message) {
  const lower = message.toLowerCase();
  for (const key of Object.keys(BOT_RESPONSES)) {
    if (key !== "default" && lower.includes(key)) return BOT_RESPONSES[key];
  }
  return BOT_RESPONSES["default"];
}

// AI Agent SVG Avatar
const AgentAvatar = ({ size = 28 }) => (
  <svg width={size} height={size} viewBox="0 0 40 40" fill="none" xmlns="http://www.w3.org/2000/svg">
    {/* Head */}
    <rect x="8" y="10" width="24" height="20" rx="6" fill="#4ade80" opacity="0.9"/>
    {/* Eyes */}
    <rect x="13" y="16" width="4" height="4" rx="2" fill="#0a0a0a"/>
    <rect x="23" y="16" width="4" height="4" rx="2" fill="#0a0a0a"/>
    {/* Eye glow */}
    <rect x="14" y="17" width="1.5" height="1.5" rx="1" fill="#4ade80"/>
    <rect x="24" y="17" width="1.5" height="1.5" rx="1" fill="#4ade80"/>
    {/* Mouth */}
    <rect x="14" y="23" width="12" height="2.5" rx="1.25" fill="#0a0a0a" opacity="0.6"/>
    {/* Antenna */}
    <rect x="19" y="5" width="2" height="6" rx="1" fill="#4ade80" opacity="0.7"/>
    <circle cx="20" cy="4" r="2" fill="#4ade80"/>
    {/* Ears */}
    <rect x="4" y="16" width="5" height="8" rx="2.5" fill="#4ade80" opacity="0.6"/>
    <rect x="31" y="16" width="5" height="8" rx="2.5" fill="#4ade80" opacity="0.6"/>
  </svg>
);

export default function AIChatWidget() {
  const [isOpen, setIsOpen] = useState(false);
  const [messages, setMessages] = useState([
    {
      id: 1,
      from: "bot",
      text: `👋 Hi! I'm ${AGENT_NAME}, fiieVe's AI assistant. How can I help you today?`,
      time: new Date(),
    },
  ]);
  const [input, setInput] = useState("");
  const [isTyping, setIsTyping] = useState(false);
  const messagesEndRef = useRef(null);

  useEffect(() => {
    if (isOpen) messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages, isOpen]);

  const sendMessage = (text) => {
    const userMsg = text || input.trim();
    if (!userMsg) return;
    setMessages((prev) => [...prev, { id: Date.now(), from: "user", text: userMsg, time: new Date() }]);
    setInput("");
    setIsTyping(true);
    setTimeout(() => {
      setIsTyping(false);
      setMessages((prev) => [...prev, { id: Date.now() + 1, from: "bot", text: getBotResponse(userMsg), time: new Date() }]);
    }, 1000 + Math.random() * 600);
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); sendMessage(); }
  };

  const formatTime = (date) => date.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });

  return (
    <>
      {/* ===== CHAT PANEL ===== */}
      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ opacity: 0, y: 20, scale: 0.95 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 20, scale: 0.95 }}
            transition={{ duration: 0.25, ease: "easeOut" }}
            className="fixed bottom-24 right-6 z-50 w-[340px] sm:w-[380px] flex flex-col rounded-2xl overflow-hidden shadow-[0_0_40px_rgba(74,222,128,0.2)] border border-green-400/30"
            style={{ maxHeight: "520px" }}
          >
            {/* Header */}
            <div className="bg-[#0d1a0f] border-b border-green-400/20 px-4 py-3 flex items-center justify-between flex-shrink-0">
              <div className="flex items-center gap-3">
                <div className="w-11 h-11 rounded-full bg-[#0a0a0a] border border-green-400/40 flex items-center justify-center flex-shrink-0">
                  <AgentAvatar size={32} />
                </div>
                <div>
                  <p className="text-white font-bold text-sm">{AGENT_NAME} — fiieVe AI</p>
                  <div className="flex items-center gap-1.5">
                    <span className="w-2 h-2 rounded-full bg-green-400 animate-pulse" />
                    <span className="text-green-400 text-xs">Online now</span>
                  </div>
                </div>
              </div>
              <button onClick={() => setIsOpen(false)} className="text-white/50 hover:text-white transition-colors p-1">
                <FaTimes />
              </button>
            </div>

            {/* Messages */}
            <div className="flex-1 overflow-y-auto bg-[#0a0a0a] px-4 py-4 space-y-3" style={{ minHeight: "280px", maxHeight: "320px" }}>
              {messages.map((msg) => (
                <div key={msg.id} className={`flex ${msg.from === "user" ? "justify-end" : "justify-start"}`}>
                  <div className={`max-w-[80%] px-4 py-2.5 rounded-2xl text-sm leading-relaxed ${
                    msg.from === "user"
                      ? "bg-green-400 text-black rounded-br-sm font-medium"
                      : "bg-[#1a1a1a] text-white/90 border border-white/10 rounded-bl-sm"
                  }`}>
                    {msg.text}
                    <p className={`text-[10px] mt-1 ${msg.from === "user" ? "text-black/50 text-right" : "text-white/30"}`}>
                      {formatTime(msg.time)}
                    </p>
                  </div>
                </div>
              ))}
              {isTyping && (
                <div className="flex justify-start">
                  <div className="bg-[#1a1a1a] border border-white/10 px-4 py-3 rounded-2xl rounded-bl-sm flex gap-1 items-center">
                    {[0, 1, 2].map((i) => (
                      <span key={i} className="w-2 h-2 rounded-full bg-green-400/70"
                        style={{ animation: `bounce 1.2s infinite ${i * 0.2}s` }} />
                    ))}
                  </div>
                </div>
              )}
              <div ref={messagesEndRef} />
            </div>

            {/* Quick Messages */}
            <div className="bg-[#0d0d0d] border-t border-white/5 px-4 py-2 flex-shrink-0">
              <p className="text-white/40 text-xs mb-2">Quick messages:</p>
              <div className="flex flex-wrap gap-1.5">
                {QUICK_MESSAGES.map((q) => (
                  <button key={q} onClick={() => sendMessage(q)}
                    className="text-xs px-3 py-1.5 rounded-full border border-green-400/30 text-green-400/80 hover:bg-green-400/10 hover:border-green-400 transition-all duration-200">
                    {q}
                  </button>
                ))}
              </div>
            </div>

            {/* Input */}
            <div className="bg-[#0d0d0d] border-t border-white/10 px-4 py-3 flex items-center gap-3 flex-shrink-0">
              <input
                type="text" value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyDown={handleKeyDown}
                placeholder="Type your message..."
                className="flex-1 bg-transparent text-white text-sm placeholder-white/30 outline-none"
              />
              <button onClick={() => sendMessage()} disabled={!input.trim()}
                className="w-8 h-8 rounded-full bg-green-400 flex items-center justify-center text-black disabled:opacity-30 hover:bg-green-300 transition-all duration-200 flex-shrink-0">
                <FaPaperPlane size={13} />
              </button>
            </div>
            <p className="bg-[#0d0d0d] text-center text-white/20 text-[10px] pb-2">
              Press Enter to send, Shift+Enter for new line
            </p>
          </motion.div>
        )}
      </AnimatePresence>

      {/* ===== TOGGLE BUTTON ===== */}
      <div className="fixed bottom-6 right-6 z-50 flex flex-col items-center gap-1">
        {/* Label above button */}
        {!isOpen && (
          <motion.div
            initial={{ opacity: 0, y: 6 }}
            animate={{ opacity: 1, y: 0 }}
            className="bg-[#0d1a0f] border border-green-400/40 text-green-400 text-xs font-semibold px-3 py-1 rounded-full shadow-lg whitespace-nowrap"
          >
            Ask {AGENT_NAME}
          </motion.div>
        )}

        <motion.button
          onClick={() => setIsOpen((p) => !p)}
          whileHover={{ scale: 1.1 }}
          whileTap={{ scale: 0.95 }}
          className="relative w-14 h-14 rounded-full bg-[#0d1a0f] border-2 border-green-400 text-black shadow-[0_0_25px_rgba(74,222,128,0.4)] flex items-center justify-center"
        >
          <AnimatePresence mode="wait">
            {isOpen ? (
              <motion.span key="close"
                initial={{ rotate: -90, opacity: 0 }} animate={{ rotate: 0, opacity: 1 }}
                exit={{ rotate: 90, opacity: 0 }} transition={{ duration: 0.2 }}>
                <FaTimes size={20} className="text-green-400" />
              </motion.span>
            ) : (
              <motion.span key="open"
                initial={{ rotate: 90, opacity: 0 }} animate={{ rotate: 0, opacity: 1 }}
                exit={{ rotate: -90, opacity: 0 }} transition={{ duration: 0.2 }}>
                <AgentAvatar size={34} />
              </motion.span>
            )}
          </AnimatePresence>

          {/* Unread dot */}
          {!isOpen && (
            <span className="absolute -top-1 -right-1 w-4 h-4 rounded-full bg-red-500 border-2 border-black flex items-center justify-center">
              <span className="text-white text-[8px] font-bold">1</span>
            </span>
          )}
        </motion.button>
      </div>

      <style>{`
        @keyframes bounce {
          0%, 60%, 100% { transform: translateY(0); }
          30% { transform: translateY(-4px); }
        }
      `}</style>
    </>
  );
}